/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Link, Typography } from '@coursera/cds-core';
import { Box, Container, ReadMore, color } from '@coursera/coursera-ui';
import { SvgChevronLeft } from '@coursera/coursera-ui/svg';

import CourseEnrollButton from 'bundles/enroll-common/components/CourseEnrollButton';
import XDPRating from 'bundles/enterprise-legacy-xdp/components/XDPRating';
import type { ProductReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import LearnerStory from 'bundles/reviews/components/LearnerStory';
import ReviewHighlights from 'bundles/reviews/components/ReviewHighlights';
import ReviewsContainer from 'bundles/reviews/components/ReviewsContainer';
import TopRatings from 'bundles/reviews/components/TopRatings';
import CDPReviewsSchemaMarkup from 'bundles/reviews/components/seo/CDPReviewsSchemaMarkup';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/reviews';

import 'css!bundles/reviews/components/__styles__/ReviewsPage';

type Props = {
  slug: string;
  courseInfo: any;
  schemaReviews: Array<ProductReview>;
  router: ReviewRouter;
};

const styles = {
  backToCDPLink: () => css`
    color: var(--cds-color-neutral-primary);

    &:hover,
    &:focus {
      color: var(--cds-color-neutral-primary);
    }
  `,
  modifiedComfy: () => css`
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
  `,
  reviewsHeader: () => css`
    margin-bottom: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-600);
  `,
};

const NUM_TOP_REVIEWS_TO_SHOW = 2;

/**
 *
 * @param {string} str - the string to strip characters from
 * @param {number} n - the character limit for stripping (e.g. n = 100 means we strip the first 100 characters)
 * @param {boolean} addEllipses - boolean determining whether we should append ellipses to the stripped string
 */
const sliceFirstNChars = (str: string, n: number, addEllipses = true) => {
  if (!str) {
    return null;
  }

  if (str.length <= n) {
    return str;
  }
  const slicedStr = str.slice(0, n);
  return addEllipses ? `${slicedStr}...` : slicedStr;
};

// Only exporting here for testing purposes, look at default export for normal use
export class ReviewsPage extends React.Component<Props> {
  filterFiveStarReviews = () => {
    const { router } = this.props;

    // this is for the link in top reviews, that says 'see 5 star reviews'
    router.replace({
      pathname: router.location.pathname,
      query: { star: '5', page: 1 },
    });
  };

  renderCDPReviewsSchemaMarkup = () => {
    const {
      courseInfo,
      schemaReviews,
      courseInfo: {
        xdpMetadata: { cdpMetadata },
        slug,
        name,
      },
    } = this.props;

    if (!courseInfo || !cdpMetadata) {
      return null;
    }

    const { ratings } = cdpMetadata;

    const CDPReviewsSchemaMarkupData = {
      aggregrateRating: {
        ratingScore: ratings && ratings.averageFiveStarRating.toFixed(1),
        ratingCount: ratings && String(ratings.ratingCount),
        reviewCount: ratings && String(ratings.commentCount),
      },
      slug,
      courseName: name,
      courseDescription: cdpMetadata.description,
      partnerName: cdpMetadata.partners[0].name,
      partnerLogoUrl: cdpMetadata.partners[0].logo || cdpMetadata.partners[0].squareLogo,
      reviews: schemaReviews,
    };

    return <CDPReviewsSchemaMarkup CDPReviewsMetaData={CDPReviewsSchemaMarkupData} />;
  };

  renderReviewsPageMetatags = () => {
    const {
      courseInfo,
      courseInfo: {
        xdpMetadata: { cdpMetadata },
      },
      router: { location },
    } = this.props;
    if (!cdpMetadata || !courseInfo) {
      return null;
    }

    const courseName = (courseInfo || {}).name;
    const { topReviews, partners } = cdpMetadata;
    let first100CharsFromTopReview;
    if ((topReviews || []).length > 0) {
      first100CharsFromTopReview = sliceFirstNChars(topReviews[0].comment, 100);
    }
    // Pick any one of the top reviews and use the first 100 chars of that in the meta tags description
    const desc = `Find helpful learner reviews, feedback, and ratings for ${courseName} from ${
      (partners && partners.length > 0 && partners[0].name) || 'Coursera'
    }. Read stories and highlights from Coursera learners who completed ${courseName} and wanted to share their experience.`;
    const description = first100CharsFromTopReview ? `${desc} ${first100CharsFromTopReview}` : desc;

    return (
      <CourseraMetatags
        metaNameAndProperties={{
          title: `Learner Reviews & Feedback for ${courseName} Course`,
          description,
          absolutePageHref: `https://${location.hostname}${location.pathname}${location.search}`,
          disableDescLimit: true,
          disableCourseraDescriptionSuffix: true,
          disableCrawlerIndexing: true,
        }}
        hrefLangOverrides={() => []}
      />
    );
  };

  render() {
    const {
      courseInfo,
      courseInfo: {
        xdpMetadata: { cdpMetadata },
      },
      router,
    } = this.props;

    // the id from courseInfo is actually the xdpProductId, which is made up of
    // productType~productId (in other words, COURSE~<courseId> or SPECIALIZATION~<s12nId>)
    const courseId = courseInfo?.id?.split('~')[1] ?? '';
    const courseName = (courseInfo || {}).name;
    const topReviewsToDisplay = cdpMetadata.topReviews?.slice(0, NUM_TOP_REVIEWS_TO_SHOW);
    const programSlug = router.params.programSlug;

    return (
      <Container rootClassName="CDPReviewsPage ReviewsPage" data-e2e="course-reviews-page">
        <div className="CourseReviewTitle border-bottom">
          <Box flexDirection="column">
            <Link
              css={styles.backToCDPLink}
              href={programSlug ? `/programs/${programSlug}/learn/${courseInfo.slug}` : `/learn/${courseInfo.slug}`}
            >
              <Box alignItems="center" rootClassName="m-t-1">
                <SvgChevronLeft size={12} />
                {courseName && (
                  <div className="font-sm m-l-1">
                    <FormattedMessage message={_t('Back to {courseName}')} courseName={courseName} />
                  </div>
                )}
              </Box>
            </Link>
            <Typography variant="d2" css={styles.reviewsHeader} color="supportText">
              <FormattedMessage
                message={_t(`Learner Reviews & Feedback for {courseName} by {partnerName}`)}
                courseName={courseName}
                partnerName={cdpMetadata.partners[0].name}
              />
            </Typography>

            {!programSlug && (
              <CourseEnrollButton
                courseId={courseId}
                css={styles.modifiedComfy}
                showStartDate={true}
                enrollButtonText={_t('Enroll for free')}
              />
            )}

            {cdpMetadata.ratings && (
              <XDPRating
                averageFiveStarRating={cdpMetadata.ratings.averageFiveStarRating}
                ratingCount={cdpMetadata.ratings.ratingCount}
                commentCount={cdpMetadata.ratings.commentCount}
                rootClassName="m-y-2"
                a11yIdentifier="Reviews"
                ratingColor={color.primaryText}
              />
            )}
          </Box>
        </div>
        <div className="AboutCourse border-bottom">
          <Typography variant="d2" className="m-y-2">
            {_t('About the Course')}
          </Typography>
          <div className="m-b-2">
            <ReadMore lines={5} more={_t('View All')} less={_t('View Less')}>
              {cdpMetadata.description}
            </ReadMore>
          </div>
        </div>
        {/* Component to use for 'write a review' feature
        <ReviewOverview overview={reviewOverview} /> */}
        {cdpMetadata.reviewHighlights && cdpMetadata.reviewHighlights.length > 0 && (
          <ReviewHighlights highlights={cdpMetadata.reviewHighlights} />
        )}
        {cdpMetadata.testimonials && cdpMetadata.testimonials.length > 0 && (
          <LearnerStory story={cdpMetadata.testimonials.elements[0]} />
        )}
        {topReviewsToDisplay && topReviewsToDisplay.length > 0 ? (
          <TopRatings ratings={topReviewsToDisplay} onStarReviewsClick={() => this.filterFiveStarReviews()} />
        ) : null}
        <ReviewsContainer courseName={courseName} courseId={courseInfo.id} />
        {this.renderReviewsPageMetatags()}
        {this.renderCDPReviewsSchemaMarkup()}
      </Container>
    );
  }
}

export default ReviewsPage;
